import { Formik } from "formik";
import { Loader } from "lucide-react";
import { Suspense, useCallback, useMemo, useState } from "react";
import { Await, useLoaderData, useNavigate, useParams } from "react-router-dom";
import tenantApi from "@/api/tenant";
import { AdminErrorNotification } from "@/components/Admin/AdminErrorNotification";
import { Checkbox } from "@/components/form/Checkbox";
import { Input } from "@/components/form/Input";
import { InputLabel } from "@/components/form/InputLabel";
import { useAppDispatch } from "@/hooks/types";
import { AdminFormContainer } from "@/pages/Admin/AdminFormContainer";
import { AdminFormFooter } from "@/pages/Admin/AdminFormFooter";
import { ReadOnlyField } from "@/pages/Admin/ReadOnlyField";
import { tenantSchema } from "@/pages/Admin/Tenants/schema/tenant";
import { addNotification } from "@/store/app/slice";
import { NotificationMessageType, NotificationType } from "@/types";
import { AdminNavigationItem } from "@/types/admin";
import { ITenant } from "@/types/tenant";
import { getIsProductionEnv } from "@/utils/env";

const tenantFormNavItems: AdminNavigationItem[] = [
    { id: "details", label: "Details" },
    { id: "images", label: "Images" },
    { id: "theme", label: "Theme" },
];

export const TenantForm = () => {
    const { getAdminTenant } = useLoaderData() as {
        getAdminTenant: Promise<ITenant>;
    };

    return (
        <Suspense fallback={<Loader />}>
            <Await
                resolve={getAdminTenant}
                errorElement={<div>Failed to load tenant</div>}
            >
                {resolvedValues => <TenantFormFields tenant={resolvedValues} />}
            </Await>
        </Suspense>
    );
};

export const TenantFormFields = ({ tenant }: { tenant?: ITenant }) => {
    const isProduction = getIsProductionEnv();
    const [activeTab, setActiveTab] = useState<string>(
        tenantFormNavItems[0].id,
    );
    const initialValues: Partial<ITenant> = useMemo(
        () => ({
            name: tenant?.name || "",
            subdomain: tenant?.subdomain || "",
            features: tenant?.features || {
                credits: false,
                ai: false,
                jobs: false,
                preferencesWizard: false,
                userJobs: false,
                atsScan: false,
            },
            images: tenant?.images || {
                primaryLogo: "",
                mobileLogo: "",
                favIcon: "",
                footerLogo: "",
            },
            theme: tenant?.theme || {
                primary: {
                    "primary-50": [],
                    "primary-100": [],
                    "primary-200": [],
                    "primary-300": [],
                    "primary-400": [],
                    "primary-500": [],
                    "primary-600": [],
                    "primary-700": [],
                    "primary-950": [],
                    "primary-800": [],
                    "primary-900": [],
                },
                gradient: {
                    "primary-from": "",
                    "primary-to": "",
                    "credits-from": "",
                    "credits-to": "",
                },
            },
            socialLinks: tenant?.socialLinks || {
                twitter: "",
                linkedin: "",
                facebook: "",
                instagram: "",
                youtube: "",
            },
            externalLinks: tenant?.externalLinks || {
                termsOfService: "",
                privacyPolicy: "",
                homepage: "",
            },
            contactEmail: tenant?.contactEmail || "",
            tagLine: tenant?.tagLine || "",
        }),
        [tenant],
    );
    const dispatch = useAppDispatch();

    const { tenantId: id } = useParams();
    const tenantId = id !== "new" ? id : undefined;

    const navigate = useNavigate();

    const handleError = useCallback(
        (err: unknown, title: string, messageType: NotificationMessageType) => {
            let errorMessage;
            if (typeof err === "string") {
                errorMessage = err;
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }

            dispatch(
                addNotification({
                    title,
                    desc: errorMessage,
                    messageType,
                    type: NotificationType.ERROR,
                }),
            );
        },
        [dispatch],
    );

    const handleSubmit = async (tenant: Partial<ITenant>) => {
        if (isProduction) {
            console.error("Cannot save tenant in production");
            return;
        }

        try {
            // Convert RGB string values to arrays before submitting
            const processedTenant = { ...tenant };

            // Process primary colors
            if (processedTenant.theme?.primary) {
                (
                    Object.keys(processedTenant.theme.primary) as Array<
                        keyof typeof processedTenant.theme.primary
                    >
                ).forEach(colorKey => {
                    const colorValue = processedTenant.theme!.primary[colorKey];
                    if (Array.isArray(colorValue)) {
                        // Already an array, skip conversion
                        return;
                    }
                    if (
                        typeof colorValue === "string" &&
                        (colorValue as string).trim()
                    ) {
                        const parts = (colorValue as string)
                            .split(",")
                            .map(s => s.trim());
                        if (parts.length === 3) {
                            const [r, g, b] = parts.map(Number);
                            if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
                                processedTenant.theme!.primary[colorKey] = [
                                    r,
                                    g,
                                    b,
                                ];
                            }
                        }
                    }
                });
            }

            // Remove the gradient processing section entirely - gradients should remain as hex strings

            console.log(processedTenant);

            if (tenantId) {
                await tenantApi
                    .updateTenant(tenantId, processedTenant)
                    .then(() => {
                        if (!tenantId) {
                            navigate("/admin/tenants", { replace: true }); // push user back to tenants list
                        }

                        dispatch(
                            addNotification({
                                title: "Successfully saved!",
                                desc: "Your details are successfully saved!!",
                                messageType:
                                    NotificationMessageType.ADMIN_TENANT_SAVE,
                                type: NotificationType.SUCCESS,
                            }),
                        );
                    });
            } else {
                await tenantApi.createTenant(processedTenant).then(() => {
                    navigate("/admin/tenants", { replace: true }); // push user back to tenants list
                });
            }
        } catch (err: unknown) {
            handleError(
                err,
                "Failed to save",
                NotificationMessageType.ADMIN_TENANT_SAVE,
            );
        }
    };

    return (
        <AdminFormContainer
            navigationItems={tenantFormNavItems}
            onNavigationItemClick={id => setActiveTab(id)}
            activeItemId={activeTab}
            pageTitle="Tenant"
        >
            <Formik
                initialValues={initialValues}
                validationSchema={tenantSchema}
                onSubmit={async values => {
                    await handleSubmit(values);
                }}
            >
                {({
                    values,
                    errors,
                    handleChange,
                    submitForm,
                    setFieldValue,
                }) => (
                    <div>
                        <div className="px-8">
                            {activeTab === "details" && (
                                <div className="flex flex-col gap-4 bg-white">
                                    {/* Basic Information */}
                                    <div className="rounded-lg border border-gray-200 px-4 py-2">
                                        <h3 className="mb-2 text-lg font-medium">
                                            Basic Information
                                        </h3>
                                        <div className="grid grid-cols-3 gap-4">
                                            <ReadOnlyField
                                                label="ID"
                                                value={tenant?.id}
                                            />
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Name"
                                                    htmlFor="name"
                                                    required
                                                />
                                                <Input
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    value={values.name}
                                                    formikChange={handleChange}
                                                    error={errors.name}
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Subdomain"
                                                    htmlFor="subdomain"
                                                    required
                                                />
                                                <Input
                                                    type="text"
                                                    name="subdomain"
                                                    id="subdomain"
                                                    value={values.subdomain}
                                                    formikChange={handleChange}
                                                    error={errors.subdomain}
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Contact Email"
                                                    htmlFor="contactEmail"
                                                />
                                                <Input
                                                    type="email"
                                                    name="contactEmail"
                                                    id="contactEmail"
                                                    value={
                                                        values.contactEmail ||
                                                        ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={errors.contactEmail}
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Tag Line"
                                                    htmlFor="tagLine"
                                                />
                                                <Input
                                                    type="text"
                                                    name="tagLine"
                                                    id="tagLine"
                                                    value={values.tagLine || ""}
                                                    formikChange={handleChange}
                                                    error={errors.tagLine}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <div className="rounded-lg border border-gray-200 px-4 py-2">
                                        <h3 className="mb-2 text-lg font-medium">
                                            Features
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="px-1 py-1">
                                                <Checkbox
                                                    id="enableCredits"
                                                    label="Enable Credits"
                                                    checked={
                                                        !!values?.features
                                                            ?.credits
                                                    }
                                                    onChange={(
                                                        checked: boolean,
                                                    ) =>
                                                        setFieldValue(
                                                            "features.credits",
                                                            checked,
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <Checkbox
                                                    id="enableAI"
                                                    label="Enable AI (not plugged in currently)"
                                                    checked={
                                                        !!values?.features?.ai
                                                    }
                                                    onChange={(
                                                        checked: boolean,
                                                    ) =>
                                                        setFieldValue(
                                                            "features.ai",
                                                            checked,
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <Checkbox
                                                    id="enableJobs"
                                                    label="Enable Jobs"
                                                    checked={
                                                        !!values?.features?.jobs
                                                    }
                                                    onChange={(
                                                        checked: boolean,
                                                    ) =>
                                                        setFieldValue(
                                                            "features.jobs",
                                                            checked,
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <Checkbox
                                                    id="enableUserJobs"
                                                    label="Enable User Jobs"
                                                    checked={
                                                        !!values?.features
                                                            ?.userJobs
                                                    }
                                                    onChange={(
                                                        checked: boolean,
                                                    ) =>
                                                        setFieldValue(
                                                            "features.userJobs",
                                                            checked,
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <Checkbox
                                                    id="enableAtsScan"
                                                    label="Enable ATS Scan"
                                                    checked={
                                                        !!values?.features
                                                            ?.atsScan
                                                    }
                                                    onChange={(
                                                        checked: boolean,
                                                    ) =>
                                                        setFieldValue(
                                                            "features.atsScan",
                                                            checked,
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <Checkbox
                                                    id="enablePreferencesWizard"
                                                    label="Enable Preferences Wizard"
                                                    checked={
                                                        !!values?.features
                                                            ?.preferencesWizard
                                                    }
                                                    onChange={(
                                                        checked: boolean,
                                                    ) =>
                                                        setFieldValue(
                                                            "features.preferencesWizard",
                                                            checked,
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* External Links */}
                                    <div className="rounded-lg border border-gray-200 px-4 py-2">
                                        <h3 className="mb-2 text-lg font-medium">
                                            External Links
                                        </h3>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Homepage"
                                                    htmlFor="externalLinks.homepage"
                                                />
                                                <Input
                                                    type="url"
                                                    name="externalLinks.homepage"
                                                    id="externalLinks.homepage"
                                                    value={
                                                        values.externalLinks
                                                            ?.homepage || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.externalLinks
                                                            ?.homepage
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Terms of Service"
                                                    htmlFor="externalLinks.termsOfService"
                                                />
                                                <Input
                                                    type="url"
                                                    name="externalLinks.termsOfService"
                                                    id="externalLinks.termsOfService"
                                                    value={
                                                        values.externalLinks
                                                            ?.termsOfService ||
                                                        ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.externalLinks
                                                            ?.termsOfService
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Privacy Policy"
                                                    htmlFor="externalLinks.privacyPolicy"
                                                />
                                                <Input
                                                    type="url"
                                                    name="externalLinks.privacyPolicy"
                                                    id="externalLinks.privacyPolicy"
                                                    value={
                                                        values.externalLinks
                                                            ?.privacyPolicy ||
                                                        ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.externalLinks
                                                            ?.privacyPolicy
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Social Links */}
                                    <div className="rounded-lg border border-gray-200 px-4 py-2">
                                        <h3 className="mb-2 text-lg font-medium">
                                            Social Links
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Twitter"
                                                    htmlFor="socialLinks.twitter"
                                                />
                                                <Input
                                                    type="url"
                                                    name="socialLinks.twitter"
                                                    id="socialLinks.twitter"
                                                    value={
                                                        values.socialLinks
                                                            ?.twitter || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.socialLinks
                                                            ?.twitter
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="LinkedIn"
                                                    htmlFor="socialLinks.linkedin"
                                                />
                                                <Input
                                                    type="url"
                                                    name="socialLinks.linkedin"
                                                    id="socialLinks.linkedin"
                                                    value={
                                                        values.socialLinks
                                                            ?.linkedin || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.socialLinks
                                                            ?.linkedin
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Facebook"
                                                    htmlFor="socialLinks.facebook"
                                                />
                                                <Input
                                                    type="url"
                                                    name="socialLinks.facebook"
                                                    id="socialLinks.facebook"
                                                    value={
                                                        values.socialLinks
                                                            ?.facebook || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.socialLinks
                                                            ?.facebook
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="Instagram"
                                                    htmlFor="socialLinks.instagram"
                                                />
                                                <Input
                                                    type="url"
                                                    name="socialLinks.instagram"
                                                    id="socialLinks.instagram"
                                                    value={
                                                        values.socialLinks
                                                            ?.instagram || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.socialLinks
                                                            ?.instagram
                                                    }
                                                />
                                            </div>
                                            <div className="px-1 py-1">
                                                <InputLabel
                                                    label="YouTube"
                                                    htmlFor="socialLinks.youtube"
                                                />
                                                <Input
                                                    type="url"
                                                    name="socialLinks.youtube"
                                                    id="socialLinks.youtube"
                                                    value={
                                                        values.socialLinks
                                                            ?.youtube || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        errors.socialLinks
                                                            ?.youtube
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "images" && (
                                <div className="flex flex-col gap-4 bg-white">
                                    <div className="rounded-lg border border-gray-200 px-4 py-2">
                                        <h3 className="mb-2 text-lg font-medium">
                                            Images
                                        </h3>
                                        <div className="grid grid-cols-2 gap-6">
                                            {/* Primary Logo */}
                                            <div className="space-y-2">
                                                <InputLabel
                                                    label="Primary Logo URL"
                                                    htmlFor="images.primaryLogo"
                                                />
                                                <Input
                                                    type="url"
                                                    name="images.primaryLogo"
                                                    id="images.primaryLogo"
                                                    value={
                                                        values.images
                                                            ?.primaryLogo || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        (errors as any).images
                                                            ?.primaryLogo
                                                    }
                                                />
                                                {values.images?.primaryLogo && (
                                                    <div className="mt-2">
                                                        <p className="mb-1 text-sm text-gray-600">
                                                            Preview:
                                                        </p>
                                                        <img
                                                            src={
                                                                values.images
                                                                    .primaryLogo
                                                            }
                                                            alt="Primary Logo"
                                                            className="h-12 max-w-full rounded border border-gray-200 object-contain"
                                                            onError={e => {
                                                                e.currentTarget.style.display =
                                                                    "none";
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Mobile Logo */}
                                            <div className="space-y-2">
                                                <InputLabel
                                                    label="Mobile Logo URL"
                                                    htmlFor="images.mobileLogo"
                                                />
                                                <Input
                                                    type="url"
                                                    name="images.mobileLogo"
                                                    id="images.mobileLogo"
                                                    value={
                                                        values.images
                                                            ?.mobileLogo || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        (errors as any).images
                                                            ?.mobileLogo
                                                    }
                                                />
                                                {values.images?.mobileLogo && (
                                                    <div className="mt-2">
                                                        <p className="mb-1 text-sm text-gray-600">
                                                            Preview:
                                                        </p>
                                                        <img
                                                            src={
                                                                values.images
                                                                    .mobileLogo
                                                            }
                                                            alt="Mobile Logo"
                                                            className="h-12 max-w-full rounded border border-gray-200 object-contain"
                                                            onError={e => {
                                                                e.currentTarget.style.display =
                                                                    "none";
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Favicon */}
                                            <div className="space-y-2">
                                                <InputLabel
                                                    label="Favicon URL"
                                                    htmlFor="images.favIcon"
                                                />
                                                <Input
                                                    type="url"
                                                    name="images.favIcon"
                                                    id="images.favIcon"
                                                    value={
                                                        values.images
                                                            ?.favIcon || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        (errors as any).images
                                                            ?.favIcon
                                                    }
                                                />
                                                {values.images?.favIcon && (
                                                    <div className="mt-2">
                                                        <p className="mb-1 text-sm text-gray-600">
                                                            Preview:
                                                        </p>
                                                        <img
                                                            src={
                                                                values.images
                                                                    .favIcon
                                                            }
                                                            alt="Favicon"
                                                            className="h-8 w-8 rounded border border-gray-200 object-contain"
                                                            onError={e => {
                                                                e.currentTarget.style.display =
                                                                    "none";
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Footer Logo */}
                                            <div className="space-y-2">
                                                <InputLabel
                                                    label="Footer Logo URL"
                                                    htmlFor="images.footerLogo"
                                                />
                                                <Input
                                                    type="url"
                                                    name="images.footerLogo"
                                                    id="images.footerLogo"
                                                    value={
                                                        values.images
                                                            ?.footerLogo || ""
                                                    }
                                                    formikChange={handleChange}
                                                    error={
                                                        (errors as any).images
                                                            ?.footerLogo
                                                    }
                                                />
                                                {values.images?.footerLogo && (
                                                    <div className="mt-2">
                                                        <p className="mb-1 text-sm text-gray-600">
                                                            Preview:
                                                        </p>
                                                        <img
                                                            src={
                                                                values.images
                                                                    .footerLogo
                                                            }
                                                            alt="Footer Logo"
                                                            className="h-12 max-w-full rounded border border-gray-200 object-contain"
                                                            onError={e => {
                                                                e.currentTarget.style.display =
                                                                    "none";
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "theme" && (
                                <div className="flex flex-col gap-4 bg-white">
                                    {/* Primary Colors */}
                                    <div className="rounded-lg border border-gray-200 px-4 py-2">
                                        <h3 className="mb-2 text-lg font-medium">
                                            Primary Colors
                                        </h3>
                                        <div className="space-y-3">
                                            {[
                                                "primary-50",
                                                "primary-100",
                                                "primary-200",
                                                "primary-300",
                                                "primary-400",
                                                "primary-500",
                                                "primary-600",
                                                "primary-700",
                                                "primary-800",
                                                "primary-900",
                                                "primary-950",
                                            ].map(colorKey => {
                                                const colorValue =
                                                    values.theme?.primary?.[
                                                        colorKey
                                                    ];
                                                let rgbString = "transparent";

                                                // Handle both array and string formats
                                                if (
                                                    Array.isArray(colorValue) &&
                                                    colorValue.length === 3
                                                ) {
                                                    rgbString = `rgb(${colorValue[0]}, ${colorValue[1]}, ${colorValue[2]})`;
                                                } else if (
                                                    typeof colorValue ===
                                                        "string" &&
                                                    colorValue.trim()
                                                ) {
                                                    // Parse comma-separated string back to RGB
                                                    const parts = colorValue
                                                        .split(",")
                                                        .map(s => s.trim());
                                                    if (parts.length === 3) {
                                                        const [r, g, b] =
                                                            parts.map(Number);
                                                        if (
                                                            !isNaN(r) &&
                                                            !isNaN(g) &&
                                                            !isNaN(b)
                                                        ) {
                                                            rgbString = `rgb(${r}, ${g}, ${b})`;
                                                        }
                                                    }
                                                }

                                                return (
                                                    <div
                                                        key={colorKey}
                                                        className="flex items-center gap-4"
                                                    >
                                                        <div className="w-40 flex-shrink-0">
                                                            <InputLabel
                                                                label={colorKey.replace(
                                                                    "primary-",
                                                                    "Primary ",
                                                                )}
                                                                htmlFor={`theme.primary.${colorKey}`}
                                                            />
                                                        </div>
                                                        <div className="w-32 flex-shrink-0">
                                                            <Input
                                                                type="text"
                                                                name={`theme.primary.${colorKey}`}
                                                                id={`theme.primary.${colorKey}`}
                                                                value={
                                                                    Array.isArray(
                                                                        colorValue,
                                                                    )
                                                                        ? colorValue.join(
                                                                              ", ",
                                                                          )
                                                                        : colorValue ||
                                                                          ""
                                                                }
                                                                formikChange={
                                                                    handleChange
                                                                }
                                                                error={
                                                                    (
                                                                        errors as any
                                                                    ).theme
                                                                        ?.primary?.[
                                                                        colorKey
                                                                    ]
                                                                }
                                                            />
                                                        </div>
                                                        <div
                                                            className="h-8 w-52 flex-shrink-0 rounded border border-gray-300"
                                                            style={{
                                                                backgroundColor:
                                                                    rgbString,
                                                            }}
                                                            title={rgbString}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Gradient Colors */}
                                    <div className="rounded-lg border border-gray-200 px-4 py-2">
                                        <h3 className="mb-2 text-lg font-medium">
                                            Gradient Colors
                                        </h3>
                                        <div className="space-y-3">
                                            {[
                                                {
                                                    key: "primary-from",
                                                    label: "Primary From Color",
                                                },
                                                {
                                                    key: "primary-to",
                                                    label: "Primary To Color",
                                                },
                                                {
                                                    key: "credits-from",
                                                    label: "Credits From Color",
                                                },
                                                {
                                                    key: "credits-to",
                                                    label: "Credits To Color",
                                                },
                                            ].map(({ key, label }) => {
                                                const colorValue =
                                                    values.theme?.gradient?.[
                                                        key
                                                    ] || "";
                                                let hexString = "transparent";

                                                // Handle hex colors
                                                if (
                                                    typeof colorValue ===
                                                        "string" &&
                                                    colorValue.trim()
                                                ) {
                                                    // Check if it's a valid hex color
                                                    if (
                                                        /^#[0-9A-F]{6}$/i.test(
                                                            colorValue.trim(),
                                                        )
                                                    ) {
                                                        hexString =
                                                            colorValue.trim();
                                                    } else if (
                                                        colorValue
                                                            .trim()
                                                            .startsWith("#")
                                                    ) {
                                                        // Try to fix incomplete hex
                                                        const cleanHex =
                                                            colorValue
                                                                .trim()
                                                                .replace(
                                                                    "#",
                                                                    "",
                                                                );
                                                        if (
                                                            cleanHex.length <= 6
                                                        ) {
                                                            hexString = `#${cleanHex.padEnd(6, "0")}`;
                                                        }
                                                    }
                                                }

                                                return (
                                                    <div
                                                        key={key}
                                                        className="flex items-center gap-4"
                                                    >
                                                        <div className="w-40 flex-shrink-0">
                                                            <InputLabel
                                                                label={label}
                                                                htmlFor={`theme.gradient.${key}`}
                                                            />
                                                        </div>
                                                        <div className="w-32 flex-shrink-0">
                                                            <Input
                                                                type="text"
                                                                name={`theme.gradient.${key}`}
                                                                id={`theme.gradient.${key}`}
                                                                placeholder="#000000"
                                                                value={
                                                                    colorValue
                                                                }
                                                                formikChange={
                                                                    handleChange
                                                                }
                                                                error={
                                                                    (
                                                                        errors as any
                                                                    ).theme
                                                                        ?.gradient?.[
                                                                        key
                                                                    ]
                                                                }
                                                            />
                                                        </div>
                                                        <div
                                                            className="h-8 w-52 flex-shrink-0 rounded border border-gray-300"
                                                            style={{
                                                                backgroundColor:
                                                                    hexString,
                                                            }}
                                                            title={hexString}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <AdminErrorNotification
                            title="Form Validation Errors"
                            errors={errors}
                            autoHide={false}
                        />
                        <AdminFormFooter
                            onSave={submitForm}
                            listHref="/admin/tenants"
                            onDelete={() => {}}
                        />
                    </div>
                )}
            </Formik>
        </AdminFormContainer>
    );
};
